import * as Yup from 'yup';
import { dotPropsToObjects, flatten, isEmptyObject, isSibblingTo, randomHex, valueAt } from './utils';
import { getI18n } from 'react-i18next';

/**
 * Creates a new schema of same type as schema in argument
 * @param schema Yup schema (only string, number, boolean and date types are supported)
 * @returns A new schema
 */
export function schemaOfType(schema: Yup.AnySchema): typeof schema {
    switch (schema.type) {
        case 'string':
            return Yup.string();
        case 'number':
            return Yup.number();
        case 'boolean':
        case 'bool':
            return Yup.boolean();
        case 'date':
            return Yup.date();

        default:
            throw new Error(`Schematype '${schema.type}' not implemented.`);
    }
}

/**
 * Adds validation that can compare value with another form value.
 * @param schema Schema to augment.
 * @param name Internal name of test.
 * @param message Validation message to show if validation fails.
 * @param test Function that tests if value is valid (returns true).
 * @returns Augmented schema.
 */
export function addTest<TValue, TFormValues>(
    schema: Yup.AnySchema,
    name: string,
    message: string,
    test: (value: TValue, form: TFormValues) => boolean
): Yup.AnySchema {
    return schema.test(name, message, function (value, ctx) {
        const [, grandparent] = ctx.from || [];
        return test(value, (grandparent || ctx.options.context).value);
    });
}

export function addComparrisonValidation<V, O>(
    schema: Yup.AnySchema,
    message: string,
    other: string,
    validate: (value: V, otherValue: O) => boolean
): Yup.AnySchema {
    return schema.test(`compare-to-${other}-${randomHex()}`, message, function test(val, ctx) {
        const otherValue = getValueFromTestContext(other, ctx);
        if (!otherValue) {
            return false;
        } else {
            return validate(val, otherValue as O);
        }
    });
}

function getValueFromTestContext<TForm extends object>(other: PathsOfType<TForm> | string, ctx: Yup.TestContext) {
    const { path, from } = ctx;
    if (isSibblingTo(path, other)) {
        const name = other.split('.').pop() as PathsOfType<TForm>;
        if (name) {
            return valueAt(ctx.parent as unknown as TForm, name);
        }
    } else if (Array.isArray(from) && from.length) {
        return valueAt(from[from.length - 1].value as TForm, other);
    }
}

export function getParentFromContext<TParent>(ctx: Yup.TestContext) {
    if (!isEmptyObject(ctx.options.context) && ctx.options.context && 'formikValues' in ctx.options.context) {
        return ctx.options.context.formikValues as TParent;
    }
    return ctx.parent as TParent;
}
const translatedLocales: Record<string, Yup.LocaleObject> = {};

export function setTranslatedLocale() {
    const t = getI18n().getFixedT(null, 'validation');
    const lang = getI18n().language;
    if (!translatedLocales[lang]) {
        const flattened = flatten({ ...Yup.defaultLocale }, '.');
        const locale = dotPropsToObjects(Object.fromEntries(Object.entries(flattened).map(([k, v]) => [k, t<string>(k, v as string)])));
        translatedLocales[lang] = locale as Yup.LocaleObject;
    }
    Yup.setLocale(translatedLocales[lang]);
}
